/**
 * Filter Configuration Registry
 *
 * Central registry of all filter configurations. This file can be imported
 * in both Server and Client Components as it contains no hooks.
 */

import {
  AGE_GROUP_FILTER,
  CLUB_FILTER_QUERY_KEYS,
  CLUB_TYPE_FILTER,
  DATE_FILTER,
  EVENT_FILTER_QUERY_KEYS,
  MEMBERSHIP_FEE_FILTER,
  MEMBERSHIP_FILTER,
  PRICE_FILTER,
  RADIUS_FILTER,
  SKILL_LEVEL_FILTER,
  EVENT_SORT,
  CLUB_SORT,
} from "./filterConstants";
import {
  ageGroupSchema,
  clubTypeSchema,
  dateSchema,
  membershipFeeSchema,
  membershipSchema,
  priceSchema,
  radiusSchema,
  skillLevelSchema,
  clubSortSchema,
  eventSortSchema,
} from "./schemas";
import type {
  EnumArrayFilterConfig,
  EnumFilterConfig,
  FilterConfig,
} from "./types";

// ==========================================
// Filter Registry
// ==========================================

export const FILTER_REGISTRY = {
  ageGroup: {
    key: "ageGroup",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(AGE_GROUP_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.ageGroup,
    schema: ageGroupSchema,
    multiSelect: true,
    category: "club",
    i18nPrefix: "quickFiltersOptions.ageGroup",
  } as EnumArrayFilterConfig<AGE_GROUP_FILTER>,

  date: {
    key: "date",
    type: "radio",
    paramType: "enum",
    values: Object.values(DATE_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.date,
    schema: dateSchema,
    multiSelect: false,
    category: "club",
    i18nPrefix: "quickFiltersOptions.date",
    defaultValue: DATE_FILTER.ANY,
  } as EnumFilterConfig<DATE_FILTER>,

  membership: {
    key: "membership",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(MEMBERSHIP_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.membership,
    schema: membershipSchema,
    multiSelect: true,
    category: "club",
    i18nPrefix: "quickFiltersOptions.membership",
  } as EnumArrayFilterConfig<MEMBERSHIP_FILTER>,

  clubType: {
    key: "clubType",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(CLUB_TYPE_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.clubType,
    schema: clubTypeSchema,
    multiSelect: true,
    category: "club",
    i18nPrefix: "quickFiltersOptions.clubType",
  } as EnumArrayFilterConfig<CLUB_TYPE_FILTER>,

  skillLevel: {
    key: "skillLevel",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(SKILL_LEVEL_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.skillLevel,
    schema: skillLevelSchema,
    multiSelect: true,
    category: "club",
    i18nPrefix: "quickFiltersOptions.skillLevel",
  } as EnumArrayFilterConfig<SKILL_LEVEL_FILTER>,

  membershipFee: {
    key: "membershipFee",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(MEMBERSHIP_FEE_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.membershipFee,
    schema: membershipFeeSchema,
    multiSelect: true,
    category: "club",
    i18nPrefix: "quickFiltersOptions.membershipFee",
  } as EnumArrayFilterConfig<MEMBERSHIP_FEE_FILTER>,

  clubSort: {
    key: "clubSort",
    type: "radio",
    paramType: "enum",
    values: Object.values(CLUB_SORT),
    queryKey: CLUB_FILTER_QUERY_KEYS.sort,
    schema: clubSortSchema,
    multiSelect: false,
    category: "club",
    i18nPrefix: "quickFiltersOptions.sort",
    defaultValue: CLUB_SORT.NEWEST_ADDED,
  } as EnumFilterConfig<CLUB_SORT>,

  price: {
    key: "price",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(PRICE_FILTER),
    queryKey: EVENT_FILTER_QUERY_KEYS.price,
    schema: priceSchema,
    multiSelect: true,
    category: "event",
    i18nPrefix: "quickFiltersOptions.price",
  } as EnumArrayFilterConfig<PRICE_FILTER>,

  eventSort: {
    key: "eventSort",
    type: "radio",
    paramType: "enum",
    values: Object.values(EVENT_SORT),
    queryKey: EVENT_FILTER_QUERY_KEYS.sort,
    schema: eventSortSchema,
    multiSelect: false,
    category: "event",
    i18nPrefix: "quickFiltersOptions.sort",
    defaultValue: EVENT_SORT.MOST_RELEVANT,
  } as EnumFilterConfig<EVENT_SORT>,

  eventSkillLevel: {
    key: "eventSkillLevel",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(SKILL_LEVEL_FILTER),
    queryKey: EVENT_FILTER_QUERY_KEYS.eventSkillLevel,
    schema: skillLevelSchema,
    multiSelect: true,
    category: "event",
    i18nPrefix: "quickFiltersOptions.skillLevel",
  } as EnumArrayFilterConfig<SKILL_LEVEL_FILTER>,

  eventAgeGroup: {
    key: "eventAgeGroup",
    type: "checkbox",
    paramType: "enumArray",
    values: Object.values(AGE_GROUP_FILTER),
    queryKey: EVENT_FILTER_QUERY_KEYS.eventAgeGroup,
    schema: ageGroupSchema,
    multiSelect: true,
    category: "event",
    i18nPrefix: "quickFiltersOptions.ageGroup",
  } as EnumArrayFilterConfig<AGE_GROUP_FILTER>,

  radius: {
    key: "radius",
    type: "radio",
    paramType: "enum",
    values: Object.values(RADIUS_FILTER),
    queryKey: CLUB_FILTER_QUERY_KEYS.radius,
    schema: radiusSchema,
    multiSelect: false,
    category: "shared",
    i18nPrefix: "quickFiltersOptions.radius",
    defaultValue: RADIUS_FILTER.KM_25,
  } as EnumFilterConfig<RADIUS_FILTER>,
} as const;

// ==========================================
// Type Exports
// ==========================================

export type FilterRegistryKey = keyof typeof FILTER_REGISTRY;

export type ClubFilterKey = Extract<
  FilterRegistryKey,
  | "ageGroup"
  | "date"
  | "membership"
  | "clubType"
  | "skillLevel"
  | "membershipFee"
  | "clubSort"
>;

export type EventFilterKey = Extract<
  FilterRegistryKey,
  "price" | "eventSort" | "eventSkillLevel" | "eventAgeGroup"
>;

// ==========================================
// Utility Functions
// ==========================================

export function getFilterConfigsByCategory(
  category: "club" | "event",
): ReadonlyArray<FilterConfig> {
  return Object.values(FILTER_REGISTRY).filter(
    (config) => config.category === category,
  );
}
